/**
 * PlacementMap — a real page capture with clickable hotspots, one per
 * sellable placement. Used on the Rate card (all spots, with prices), on
 * the public partnership page and the brand deal view (only the spots in
 * the offer highlighted, the rest dimmed).
 *
 * Pure presentation: the caller decides what a hotspot says via `detail`.
 */
import { CSSProperties, ReactNode, useState } from 'react';
import { Hotspot, MapPage } from '../../lib/placementMap';

interface Props {
  page: MapPage;
  /** Rate keys to highlight. Undefined = all spots equal. */
  highlight?: Set<string>;
  /** Hide spots that are not highlighted instead of dimming them. */
  onlyHighlighted?: boolean;
  /** Text shown in the pin's bubble for a spot. */
  detail?: (spot: Hotspot) => ReactNode;
  /** Accent colour for highlighted pins. */
  accent?: string;
  /** Tone of the dimmed overlay / pins, for light vs editorial pages. */
  ink?: string;
  /** Max rendered height; the image scrolls inside when taller. */
  maxHeight?: number;
  style?: CSSProperties;
}

export const PlacementMap = ({
  page, highlight, onlyHighlighted, detail, accent = '#ffbd09', ink = '#2b4138', maxHeight, style,
}: Props) => {
  const [active, setActive] = useState<number | null>(null);
  const spots = page.spots
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => !onlyHighlighted || !highlight || highlight.has(s.rate));

  return (
    <div style={{ position: 'relative', borderRadius: 12, overflow: maxHeight ? 'auto' : 'hidden', maxHeight, border: `1px solid ${ink}1f`, background: '#f3f2ec', ...style }}>
      <div style={{ position: 'relative', aspectRatio: `${page.width} / ${page.height}` }}>
        <img
          src={page.src}
          alt={`${page.title} on lamptwist.com`}
          loading="lazy"
          style={{ display: 'block', width: '100%', height: 'auto' }}
        />
        {spots.map(({ s, i }) => {
          const on = !highlight || highlight.has(s.rate);
          const isActive = active === i;
          return (
            <button
              key={i}
              type="button"
              aria-label={s.label}
              onClick={() => setActive(isActive ? null : i)}
              onMouseEnter={() => setActive(i)}
              onMouseLeave={() => setActive((a) => (a === i ? null : a))}
              style={{
                position: 'absolute',
                left: `${s.x}%`, top: `${s.y}%`, width: `${s.w}%`, height: `${s.h}%`,
                border: `2px ${s.ghost ? 'dashed' : 'solid'} ${on ? accent : `${ink}55`}`,
                background: on ? `${accent}${isActive ? '33' : '1a'}` : `${ink}0d`,
                borderRadius: 6,
                cursor: 'pointer',
                padding: 0,
                boxSizing: 'border-box',
                transition: 'background .15s',
                zIndex: isActive ? 3 : 1,
              }}
            >
              <span
                style={{
                  position: 'absolute', left: 8, top: 8,
                  background: on ? accent : ink, color: on ? ink : '#fff',
                  fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                  padding: '3px 8px', borderRadius: 999, whiteSpace: 'nowrap',
                  fontFamily: 'inherit',
                }}
              >
                {s.label}{s.ghost ? ' · when booked' : ''}
              </span>
              {isActive && detail && (
                <span
                  style={{
                    position: 'absolute', left: 8, top: 36, maxWidth: 'min(320px, 90%)',
                    background: '#fff', color: ink, textAlign: 'left',
                    fontSize: 12.5, lineHeight: 1.45, padding: '10px 12px', borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(0,0,0,.14)', fontWeight: 400, fontFamily: 'inherit',
                  }}
                >
                  {detail(s)}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PlacementMap;
